import { MenuItem, Modifier, SelectedOption } from '../services/customerApi';

export interface CartLine {
  lineId: string;
  itemId: string;
  vendorId: string;
  vendorName: string;
  name: string;
  basePrice: number;
  unitPrice: number;
  quantity: number;
  image?: string | null;
  selectedOptions: SelectedOption[];
  customizations: string[];
  note?: string;
}

export function getBasePrice(item: MenuItem): number {
  return item.is_promo && item.promo_price != null ? item.promo_price : item.price;
}

export function selectionsToOptions(selections: Record<string, string[]>): SelectedOption[] {
  return Object.entries(selections).map(([modifierId, optionIds]) => ({
    modifierId,
    optionIds,
  }));
}

export function buildCustomizations(
  modifiers: Modifier[],
  selections: Record<string, string[]>,
  note?: string
): string[] {
  const lines = modifiers
    .map((modifier) => {
      const selectedIds = selections[modifier.id] || [];
      const selectedOptions = modifier.options.filter((option) => selectedIds.includes(option.id));
      if (selectedOptions.length === 0) return null;
      return `${modifier.name}: ${selectedOptions.map((option) => option.name).join(', ')}`;
    })
    .filter(Boolean) as string[];

  const trimmedNote = note?.trim();
  if (trimmedNote) {
    lines.push(`Note: ${trimmedNote}`);
  }

  return lines;
}

export function computeUnitPrice(
  item: MenuItem,
  modifiers: Modifier[],
  selections: Record<string, string[]>
): number {
  let total = getBasePrice(item);

  modifiers.forEach((modifier) => {
    const selectedIds = selections[modifier.id] || [];
    modifier.options.forEach((option) => {
      if (selectedIds.includes(option.id)) {
        total += option.priceAdjustment;
      }
    });
  });

  return total;
}

export function lineSignature(line: Pick<CartLine, 'itemId' | 'selectedOptions' | 'note'>): string {
  return JSON.stringify({
    itemId: line.itemId,
    selectedOptions: line.selectedOptions,
    note: line.note || '',
  });
}

export function optionsToSelections(selectedOptions: SelectedOption[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  selectedOptions.forEach(({ modifierId, optionIds }) => {
    result[modifierId] = optionIds;
  });
  return result;
}

export function extractNoteFromCustomizations(customizations: string[]): string {
  const noteLine = customizations.find((line) => line.startsWith('Note: '));
  return noteLine ? noteLine.slice(6) : '';
}

export function getSimpleLineQuantity(lines: CartLine[], itemId: string): number {
  return lines
    .filter((line) => line.itemId === itemId && line.selectedOptions.length === 0 && !line.note)
    .reduce((sum, line) => sum + line.quantity, 0);
}

export function createCartLine(input: {
  item: MenuItem;
  vendorId: string;
  vendorName: string;
  quantity: number;
  unitPrice: number;
  selectedOptions?: SelectedOption[];
  customizations?: string[];
  note?: string;
}): CartLine {
  const { item, vendorId, vendorName, quantity, unitPrice, selectedOptions = [], customizations = [], note } = input;
  return {
    lineId: crypto.randomUUID(),
    itemId: item.id,
    vendorId,
    vendorName,
    name: item.name,
    basePrice: getBasePrice(item),
    unitPrice,
    quantity,
    image: item.image ?? null,
    selectedOptions,
    customizations,
    note,
  };
}
